// src/layout/ProjectSwitcher.jsx
//
// Topbar project switcher. Reads the Engagements list off WorkspaceContext,
// already scoped server-side to whichever Account is currently selected —
// refetches automatically whenever that selection changes. Selecting an
// entry calls selectEngagement(), which is what actually drives
// project-scoped permissions/nav gating elsewhere in the app (see
// WorkspaceContext.jsx) — this isn't just a visual pill anymore.
//
// No "All Projects"/clear-selection entry — WorkspaceContext already
// auto-selects the first Engagement whenever the current Account has any
// (see syncAccountData), so there's always a real Project selected once one
// exists; Topbar.jsx hides this switcher entirely when there are none.
//
// Registering a new Project is Admin > Projects' job (see
// accounts/pages/ProjectsPage.jsx) — this switcher only selects among
// Projects that already exist.

import PropTypes from "prop-types";
import { FolderGit2, ChevronsUpDown, Check, RefreshCw } from "lucide-react";
import { useWorkspace } from "../context/WorkspaceContext";

export default function ProjectSwitcher({ open, onToggle }) {
  const { engagements, loading, error, currentEngagementId, selectEngagement, refresh } = useWorkspace();

  const selectedLabel =
    engagements.find((e) => e.engagement_id === currentEngagementId)?.client_name ||
    (loading ? "Loading…" : "Select project");

  return (
    <div style={{ position: "relative" }}>
      <button type="button" className="orion-pill-btn" onClick={onToggle}>
        <span style={{ width: 15, height: 15, color: "var(--jade-neutral-500)", display: "flex" }}>
          <FolderGit2 size={15} />
        </span>
        <span className="orion-pill-label">{selectedLabel}</span>
        <span className="orion-chevud"><ChevronsUpDown size={14} /></span>
      </button>

      {open && (
        <div className="orion-dropmenu" style={{ width: 280 }}>
          <div className="orion-drop-hd">Project</div>

          {error ? (
            <button type="button" className="orion-drop-row" onClick={() => refresh()}>
              <RefreshCw size={14} />
              <div className="meta"><div className="t">Couldn't load projects — retry</div></div>
            </button>
          ) : loading ? (
            <div className="orion-drop-row disabled">
              <div className="meta"><div className="t">Loading projects…</div></div>
            </div>
          ) : (
            engagements.map((engagement) => (
              <button
                key={engagement.engagement_id}
                type="button"
                className={`orion-drop-row${engagement.engagement_id === currentEngagementId ? " on" : ""}`}
                onClick={() => {
                  selectEngagement(engagement.engagement_id);
                  onToggle();
                }}
              >
                <FolderGit2 size={16} style={{ color: "var(--jade-blue-700)", flex: "0 0 auto" }} />
                <div className="meta"><div className="t">{engagement.client_name}</div></div>
                {engagement.engagement_id === currentEngagementId && (
                  <span className="check"><Check size={16} /></span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
ProjectSwitcher.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
